import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SystemUser, availableRoles } from '@/mocks/accessControl';

interface UserTableProps {
  users: SystemUser[];
  onRoleChange: (user: SystemUser, newRole: string) => void;
  onToggleStatus: (user: SystemUser) => void;
  onResendDetails: (user: SystemUser) => void;
  onSendDetails: (user: SystemUser) => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

function getAvatarBg(name: string): string {
  const colors = [
    'bg-primary-500',
    'bg-accent-500',
    'bg-secondary-500',
    'bg-foreground-600',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export default function UserTable({
  users,
  onRoleChange,
  onToggleStatus,
  onResendDetails,
  onSendDetails,
}: UserTableProps) {
  const { t } = useTranslation();
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editRoleValue, setEditRoleValue] = useState('');

  const startEditing = (user: SystemUser) => {
    setEditingUserId(user.id);
    setEditRoleValue(user.role);
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditRoleValue('');
  };

  const saveRole = (user: SystemUser) => {
    if (editRoleValue && editRoleValue !== user.role) {
      onRoleChange(user, editRoleValue);
    }
    setEditingUserId(null);
    setEditRoleValue('');
  };

  if (users.length === 0) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-12 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
          <i className="ri-shield-user-line text-2xl text-foreground-400" />
        </div>
        <h3 className="font-heading font-700 text-foreground-900 text-lg mb-1">{t('accessControl.noUsersFound')}</h3>
        <p className="text-foreground-500 text-sm font-label">{t('accessControl.tryAdjustingFilters')}</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-100/50">
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('accessControl.userName')}
              </th>
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('accessControl.email')}
              </th>
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('accessControl.phone')}
              </th>
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('accessControl.role')}
              </th>
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('common.status')}
              </th>
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('accessControl.lastLogin')}
              </th>
              <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, idx) => {
              const isEditing = editingUserId === user.id;
              return (
                <tr
                  key={user.id}
                  className={`border-b border-background-100 hover:bg-background-100/50 transition-colors ${
                    idx === users.length - 1 ? 'border-b-0' : ''
                  }`}
                >
                  {/* User Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full ${getAvatarBg(user.name)} text-background-50 text-xs font-heading font-700`}
                      >
                        {getInitials(user.name)}
                      </div>
                      <span className="text-sm font-medium text-foreground-900 font-label whitespace-nowrap">
                        {user.name}
                      </span>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-600 font-label whitespace-nowrap">
                      {user.email}
                    </span>
                  </td>

                  {/* Phone */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-600 font-label whitespace-nowrap">
                      {user.phone}
                    </span>
                  </td>

                  {/* Role */}
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={editRoleValue}
                          onChange={(e) => setEditRoleValue(e.target.value)}
                          className="xl-input cursor-pointer text-sm py-1.5 appearance-none pr-7 min-w-[140px]"
                        >
                          {availableRoles.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => saveRole(user)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
                          title={t('accessControl.saveRole')}
                        >
                          <i className="ri-check-line text-sm" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-background-200 text-foreground-500 hover:bg-background-300 hover:text-foreground-700 transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-label font-medium bg-secondary-100 text-secondary-800 whitespace-nowrap">
                          {user.role}
                        </span>
                        <button
                          onClick={() => startEditing(user)}
                          className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
                          title={t('accessControl.editRole')}
                        >
                          <i className="ri-pencil-line text-xs" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-label font-medium whitespace-nowrap ${
                        user.status === 'active'
                          ? 'bg-semantic-success-bg text-semantic-success'
                          : user.status === 'pending'
                            ? 'bg-semantic-warning-bg text-semantic-warning'
                            : 'bg-background-200 text-foreground-500'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          user.status === 'active'
                            ? 'bg-semantic-success'
                            : user.status === 'pending'
                              ? 'bg-semantic-warning'
                              : 'bg-foreground-400'
                        }`}
                      />
                      {user.status === 'active'
                        ? t('common.active')
                        : user.status === 'pending'
                          ? t('common.pending')
                          : t('common.inactive')}
                    </span>
                  </td>

                  {/* Last Login */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground-500 font-label whitespace-nowrap">
                      {user.lastLogin}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* Send / Resend Details */}
                      {user.status === 'pending' ? (
                        <button
                          onClick={() => onSendDetails(user)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-primary-500 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer"
                          title={t('accessControl.sendDetails')}
                        >
                          <i className="ri-mail-send-line text-base" />
                        </button>
                      ) : (
                        <button
                          onClick={() => onResendDetails(user)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
                          title={t('accessControl.resendDetails')}
                        >
                          <i className="ri-mail-send-line text-base" />
                        </button>
                      )}

                      {/* Toggle Active/Inactive */}
                      {user.status === 'active' ? (
                        <button
                          onClick={() => onToggleStatus(user)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-semantic-warning hover:text-semantic-warning/80 hover:bg-semantic-warning-bg transition-colors cursor-pointer"
                          title={t('accessControl.deactivateUser')}
                        >
                          <i className="ri-forbid-line text-base" />
                        </button>
                      ) : user.status === 'inactive' ? (
                        <button
                          onClick={() => onToggleStatus(user)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-semantic-success hover:text-semantic-success/80 hover:bg-semantic-success-bg transition-colors cursor-pointer"
                          title={t('accessControl.activateUser')}
                        >
                          <i className="ri-checkbox-circle-line text-base" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}